import { ReactNode, useEffect, useMemo, useRef } from "react";
import { LDProvider as Provider, ProviderConfig, useLDClient } from "launchdarkly-react-client-sdk";
import SessionReplay from "@launchdarkly/session-replay";
import { useCurrentUser } from "../hooks/use-currrent-user";

const APPLICATION_CONTEXT = { key: "internal", name: "Adora Internal" } as const;

// When the user object becomes available, re-identify to load flags.
const FlagReloader = () => {
  const { user } = useCurrentUser();
  const previousUserRef = useRef<typeof user | null>(null);
  const ldClient = useLDClient();

  useEffect(() => {
    if ((!previousUserRef.current || previousUserRef.current.id === undefined) && user?.id) {
      ldClient?.identify({
        kind: "multi",
        user: {
          key: user.id,
          role: user.role
        },
        application: APPLICATION_CONTEXT
      });
    }

    previousUserRef.current = user;
  }, [user, ldClient]);

  return null;
};

const LDProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useCurrentUser();
  const appEnv = import.meta.env.VITE_APP_ENV;
  const shouldEnableLaunchDarklyTelemetry = appEnv === "staging" || appEnv === "production";
  const clientSideID = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID;

  const ldConfig: ProviderConfig = useMemo(
    () => ({
      clientSideID,
      context: {
        kind: "multi",
        user: {
          key: user?.id || "unknown",
          role: user?.role
        },
        application: APPLICATION_CONTEXT
      },
      options: {
        bootstrap: "localStorage" as const,
        plugins: shouldEnableLaunchDarklyTelemetry ? [new SessionReplay({ privacySetting: "default" })] : []
      },
      timeout: 5
    }),
    [clientSideID, shouldEnableLaunchDarklyTelemetry, user?.id, user?.role]
  );

  return (
    <Provider {...ldConfig}>
      <FlagReloader />
      {children}
    </Provider>
  );
};

export default LDProvider;
